import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, CheckCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useHCaptchaSitekey, verifyHCaptchaToken } from "@/hooks/useHCaptcha";

const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha | null>(null);
  const sitekey = useHCaptchaSitekey();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    if (sitekey && !captchaToken) {
      toast({ title: "Verification required", description: "Please complete the captcha.", variant: "destructive" });
      return;
    }

    setStatus("loading");

    if (sitekey && captchaToken) {
      const result = await verifyHCaptchaToken(captchaToken, "newsletter");
      if (!result.success) {
        toast({ title: "Captcha failed", description: result.reason || "Please try again.", variant: "destructive" });
        captchaRef.current?.resetCaptcha();
        setCaptchaToken(null);
        setStatus("idle");
        return;
      }
    }

    const { error } = await supabase
      .from("newsletter_subscribers" as any)
      .insert({ email } as any);

    if (error) {
      if (error.code === "23505") {
        toast({ title: "Already subscribed", description: "This email is already on our list." });
      } else {
        toast({ title: "Error", description: "Could not subscribe. Try again later.", variant: "destructive" });
      }
      setStatus("idle");
      return;
    }

    setStatus("success");
    setEmail("");
    captchaRef.current?.resetCaptcha();
    setCaptchaToken(null);
    toast({ title: "Subscribed!", description: "You'll receive sovereign-grade intelligence updates." });
  };

  return (
    <section className="px-6 py-16 max-w-[1200px] mx-auto">
      <div className="panel-tactical p-8 md:p-12 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sovereign-gold/5 via-transparent to-ai-cyan/5 pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sovereign-gold/20 bg-sovereign-gold/5 text-sovereign-gold text-xs font-mono mb-4">
            <Mail className="w-3 h-3" /> INTELLIGENCE BRIEFING
          </div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">
            Stay in the <span className="text-gradient-gold">Loop</span>
          </h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
            Sovereign-grade updates on new modules, AI capabilities, and platform intelligence — delivered to your inbox.
          </p>

          {status === "success" ? (
            <div className="flex items-center justify-center gap-2 text-tactical-green font-mono text-sm animate-fade-in-up">
              <CheckCircle className="w-5 h-5" /> SUBSCRIPTION CONFIRMED
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-card border-border rounded-full text-sm flex-1"
                disabled={status === "loading"}
              />
              <Button
                type="submit"
                variant="sovereign"
                className="gap-2 rounded-full w-full sm:w-auto"
                disabled={status === "loading"}
              >
                {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Subscribe
              </Button>
            </form>
          )}
          {sitekey && status !== "success" && (
            <div className="mt-4 flex justify-center">
              <HCaptcha
                ref={captchaRef}
                sitekey={sitekey}
                theme="dark"
                size="normal"
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken(null)}
              />
            </div>
          )}
          <p className="text-[10px] text-muted-foreground mt-3 font-mono">No spam. Unsubscribe anytime. Zero-knowledge compliant.</p>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
